import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CardBrandLogo from '@/modules/billing/components/CardBrandLogo';
import { PaymentMethod } from '@/modules/billing/models/PaymentMethod';
import {
  CheckBadgeIcon,
  CheckIcon,
  NoSymbolIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const maskLast4 = (last4?: string) => (last4 ? `•••• ${last4}` : '•••• ••••');

const formatExpiry = (expMonth?: number, expYear?: number) => {
  if (!expMonth || !expYear) return undefined;
  const mm = expMonth.toString().padStart(2, '0');
  const yy = (expYear % 100).toString().padStart(2, '0');
  return `${mm}/${yy}`;
};

const formatBrand = (brand?: string) => {
  if (!brand) return 'Card';
  const b = brand.toLowerCase();
  if (b === 'amex') return 'American Express';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

interface PaymentMethodCardProps {
  method: PaymentMethod;
  onDelete?: (id: string) => void;
  onDisable?: (id: string) => void;
  onEnable?: (id: string) => void;
  onSetDefault?: (id: string) => void;
  isLoadingEnable?: boolean;
  isLoadingDisable?: boolean;
  isLoadingDelete?: boolean;
  iseLoadingSetDefault?: boolean;
}

const PaymentMethodCard = ({
  method,
  onDelete = () => {},
  onDisable = () => {},
  onEnable = () => {},
  onSetDefault = () => {},
  isLoadingEnable = false,
  isLoadingDisable = false,
  isLoadingDelete = false,
  iseLoadingSetDefault = false,
}: PaymentMethodCardProps) => {
  if (!method.card) return null;

  const card = method.card;

  const brand = card.brand;
  const last4 = card.last4;
  const expiry = formatExpiry(card.expMonth, card.expYear);

  const isDefault = Boolean(card.isDefault);
  const isActive = Boolean(card.isActive);

  const canDisable = isActive && !isDefault;
  const canEnable = !isActive;
  const canDelete = method.isDeletable;

  const canSetDefault = isActive && !isDefault;

  const cardLabel = `${formatBrand(brand)} ${maskLast4(last4)}`;

  return (
    <div
      className="rounded-lg border bg-white p-4 shadow-sm"
      data-testid="payment-method-card"
      data-payment-method-id={method.id}
    >
      <div className="flex items-start justify-between gap-3 flex-col lg:flex-row">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-12 w-14 shrink-0 items-center justify-center">
            <CardBrandLogo brand={brand} />
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="min-w-0 truncate text-sm font-semibold text-gray-900"
                data-testid="payment-method-card-label"
              >
                {cardLabel}
              </span>

              {isDefault && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
                  data-testid="payment-method-default-badge"
                >
                  <CheckBadgeIcon className="h-4 w-4" />
                  Default
                </span>
              )}

              {isActive ? (
                <span
                  className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                  data-testid="payment-method-active-badge"
                >
                  Active
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                  data-testid="payment-method-disabled-badge"
                >
                  <NoSymbolIcon className="h-4 w-4" />
                  Disabled
                </span>
              )}
            </div>

            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
              {expiry && <span>Expires {expiry}</span>}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {!isDefault && (
            <Confirm
              onConfirm={() => onSetDefault(method.id)}
              title={
                !isActive ? 'Enable payment method to set as default' : 'Set default payment method'
              }
              message={
                !isActive
                  ? `${cardLabel} is disabled. Enable it before setting it as default.`
                  : `Do you want to set ${cardLabel} as your default payment method?`
              }
            >
              {(handleClick) => (
                <Button
                  onClick={() => canSetDefault && handleClick()}
                  disabled={!canSetDefault}
                  variant="secondary"
                  icon={<StarIcon className="h-4 w-4" />}
                  loading={iseLoadingSetDefault}
                  data-testid="payment-method-set-default-button"
                >
                  Set as default
                </Button>
              )}
            </Confirm>
          )}

          {isActive ? (
            <Confirm
              onConfirm={() => onDisable(method.id)}
              title={
                isDefault ? 'Cannot disable the default payment method' : 'Disable payment method'
              }
              message={`Do you want to disable ${cardLabel}?`}
            >
              {(handleClick) => (
                <Button
                  onClick={() => canDisable && handleClick()}
                  disabled={!canDisable}
                  variant="secondary"
                  icon={<NoSymbolIcon className="h-4 w-4" />}
                  loading={isLoadingDisable}
                  data-testid="payment-method-disable-button"
                >
                  Disable
                </Button>
              )}
            </Confirm>
          ) : (
            <Confirm
              onConfirm={() => onEnable(method.id)}
              title="Enable payment method"
              message={`Do you want to enable ${cardLabel}?`}
            >
              {(handleClick) => (
                <Button
                  onClick={() => canEnable && handleClick()}
                  disabled={!canEnable}
                  variant="secondary"
                  icon={<CheckIcon className="h-4 w-4" />}
                  loading={isLoadingEnable}
                  data-testid="payment-method-enable-button"
                >
                  Enable
                </Button>
              )}
            </Confirm>
          )}

          <Confirm
            onConfirm={() => onDelete(method.id)}
            title={canDelete ? 'Delete payment method' : 'Cannot delete the default payment method'}
            message={`Do you want to delete ${cardLabel}?`}
          >
            {(handleClick) => (
              <Button
                onClick={() => canDelete && handleClick()}
                disabled={!canDelete}
                variant="danger"
                icon={<TrashIcon className="h-4 w-4" />}
                loadingIconClassName="w-5 h-5 mr-1 fill-red-500"
                loading={isLoadingDelete}
                data-testid="payment-method-delete-button"
              >
                Delete
              </Button>
            )}
          </Confirm>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodCard;
